import { useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";

import { useAppState } from "../../../../../../../context/StateContext";
import { PdfReport as ReportContent } from "../../../../../../../models/pdf.report";
import { LocationStatusReport } from "../../../../../../../models/report.location.status";
import { Filters } from "../../../../../../../models/shipeezi.table";
import { StorageBoxType } from "../../../../../../../models/storage.box.type";
import { Warehouse } from "../../../../../../../models/warehouse";
import { MapCell } from "../../../../../../../models/warehouse.mapcell";
import StorageBoxTypeService from "../../../../../../../services/storage.box.type.service";
import WarehouseMapCellService from "../../../../../../../services/warehouse.map.cell.service";
import WarehouseReportService, {
  GenerateWarehouse,
  LocationStatusReportCSV,
} from "../../../../../../../services/warehouse.report.service";
import WarehouseService from "../../../../../../../services/warehouse.service";
import { FileTypes, Status, TypeReportWarehouse } from "../../../../../../../util/enums";
import { handleGeneratePdf } from "../../../../../../../util/generateReport";

export const useLocationsActions = () => {
  const { currentUser } = useAppState();

  const [storageBoxTypesList, setStorageBoxTypesList] = useState<StorageBoxType[]>([]);
  const [warehousesList, setWarehousesList] = useState<Warehouse[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [locationsReports, setLocationsReports] = useState<LocationStatusReport[]>([]);
  const [mapCellSelected, setMapCellSelected] = useState<MapCell | null>(null);
  const [filters, setFilters] = useState<Filters>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);

  const locationsFilters: GenerateWarehouse = {
    type: TypeReportWarehouse.LOCATION_STATUS,
    warehouses: selectedWarehouseId
      ? warehousesList.filter((warehouse) => warehouse?.id === selectedWarehouseId)
      : undefined,
    mapCells: mapCellSelected ? [mapCellSelected] : undefined,
    status: filters && filters?.status ? (filters?.status as Array<"In Use" | "Empty">) : undefined,
    storageBoxTypes:
      filters && filters?.storageBoxType && storageBoxTypesList?.length
        ? storageBoxTypesList.filter((type) =>
            ((filters?.storageBoxType as string[]) || []).includes(type.name)
          )
        : undefined,
  };

  const getLocations = () =>
    useQuery(
      [
        "locations.status.reports.list",
        filters,
        selectedWarehouseId,
        mapCellSelected,
        page,
        pageSize,
      ],
      () => WarehouseReportService.getLocationsStatusReports(locationsFilters, { page, pageSize }),
      {
        onSuccess: (response) => {
          if (response && response?.data) {
            setTotal(response.data.count);
            setLocationsReports(response.data.rows);
          }
        },
      }
    );

  const getStorageBoxTypesList = () =>
    useQuery(
      ["storage.box.types.list", currentUser?.companyId],
      () =>
        StorageBoxTypeService.getAll({
          isActive: "1",
          companyId: currentUser?.companyId,
          sortBy: JSON.stringify([["name", "asc"]]),
        }),
      {
        enabled: !!currentUser?.companyId,
        onSuccess: (response) => {
          if (response && response.data) {
            setStorageBoxTypesList(response.data);
          }
        },
      }
    );

  const getWarehousesList = () =>
    useQuery(
      ["warehouse.list", currentUser?.companyId, currentUser?.branches],
      () =>
        WarehouseService.getAll({
          companyId: currentUser?.companyId,
          status: Status.ACTIVE,
          sortBy: JSON.stringify([["name", "asc"]]),
          include: JSON.stringify([
            {
              association: "branches",
              where: {
                id: currentUser?.branches?.map((branch) => branch.id),
              },
            },
          ]),
        }),
      {
        enabled: !!currentUser?.companyId,
        onSuccess: (response) => {
          if (response && response.data && response.data.length > 0) {
            setWarehousesList(response.data);
            setSelectedWarehouseId(response.data[0]?.id || 0);
          }
        },
      }
    );

  const getMapCells = (warehouseId: number, name?: string) =>
    WarehouseMapCellService.getAll({
      include: JSON.stringify([
        {
          attributes: ["id", "name"],
          association: "map",
          include: [
            { association: "section", where: { warehouseId }, attributes: ["warehouseId", "name"] },
          ],
          require: true,
          where: { isActive: true },
        },
      ]),
      isActive: 1,
      searchByOr: JSON.stringify([{ name: name || "" }]),
      sortBy: JSON.stringify([["name"], ["map", "name", "asc"]]),
    });

  const useGenerateLocationsReport = () =>
    useMutation(
      ["warehouse.report.generate"],
      (generateParams: GenerateWarehouse) => WarehouseReportService.generate(generateParams),
      {
        onSuccess: (response) => {
          if (response?.data && response?.data?.content) {
            const { content } = response.data;

            switch (response?.data?.type) {
              case FileTypes.PDF:
                if (typeof content?.[0] === "number")
                  handleGeneratePdf({
                    data: response.data as ReportContent,
                    exportFile: true,
                  });
                break;
              default:
                break;
            }
          }
        },
      }
    );

  const useExportLocationStatusCSVData = (
    onSuccessCallback: (data: LocationStatusReportCSV[]) => void
  ) => {
    return useMutation({
      mutationKey: ["export.location.status.csv", locationsFilters],
      mutationFn: async () => {
        return WarehouseReportService.getLocationStatusExportCSVData(locationsFilters);
      },
      onSuccess: (response) => {
        if (response?.data?.locationStatusCSVData && response?.data?.locationStatusCSVData?.length) {
          onSuccessCallback(response.data.locationStatusCSVData);
        }
      },
    });
  };

  return {
    getLocations,
    getMapCells,
    getStorageBoxTypesList,
    getWarehousesList,
    locationsFilters,
    locationsReports,
    mapCellSelected,
    page,
    pageSize,
    selectedWarehouseId,
    setFilters,
    setMapCellSelected,
    setPage,
    setPageSize,
    setSelectedWarehouseId,
    storageBoxTypesList,
    total,
    useExportLocationStatusCSVData,
    useGenerateLocationsReport,
    warehousesList,
  };
};
